import { BookingPreparationSchema } from "./booking-recovery.schema";
import { Schema as S } from "effect";

/**
 * Request to book an appointment.
 * @since 1.1.8
 * @example BookAppointmentRequestSchema;
 */
export const BookAppointmentRequestSchema = S.Struct({
  /** Patient ID */
  patientId: S.String,
  /** Appointment type ID (scheduletype) */
  appointmentTypeId: S.String,
  /** Calendar ID for the appointment */
  calendarId: S.String,
  /** Start time (ISO 8601) */
  startTime: S.String,
  /** End time (ISO 8601) */
  endTime: S.String,
  /** Optional patient-entered description or scheduling note */
  description: S.optional(S.String),
  /**
   * Client-minted id identifying one booking intent.
   *
   * Reused across retries of the same intent so a double tap, a reload, or a
   * retry after an unanswered request cannot create a second appointment.
   * Optional for compatibility with callers that predate idempotency; those
   * callers keep the previous at-least-once behaviour.
   */
  clientSubmissionId: S.optional(S.String),
});

/** Shared scheduling contract for BookAppointmentRequest.
 * @since 1.1.8
 * @example BookAppointmentRequest;
 */
export type BookAppointmentRequest = S.Schema.Type<
  typeof BookAppointmentRequestSchema
>;

/** Receipt-bound new booking request, distinct from receipt-only recovery status.
 * The coordinator requires preparation before dispatch; questionnaire fields are
 * added at their contract owner without granting status authority to create.
 * @since 1.1.8
 * @example BookingCreateRequestSchema;
 */
export const BookingCreateRequestSchema = S.Struct({ ...BookAppointmentRequestSchema.fields, ...BookingPreparationSchema.fields });
/** Typed receipt-bound new booking request.
 * @since 1.1.8
 * @example const request: BookingCreateRequest = { patientId: "p", appointmentTypeId: "t", calendarId: "c", startTime: "2026-10-05T09:00:00.000Z", endTime: "2026-10-05T09:30:00.000Z", clientSubmissionId: "s", recoveryReceipt: "ab".repeat(32) };
 */
export type BookingCreateRequest = S.Schema.Type<typeof BookingCreateRequestSchema>;
